import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { IIdeMessagingService } from '@edusense/shared-types-ide';
import type { INavigationService } from '@edusense/shared-types-navigation';
import type { ITokenManager } from '@edusense/shared-types-auth';
import type { AuthState } from '@edusense/shared-types-auth';
import type { User } from '@edusense/shared-types-user';
import type { LoginCredentials, RegisterData } from '@edusense/shared-types-auth';
import { tokenManager } from '../token-manager';
import { createAuthAxiosInstance } from '../axios/auth-axios-instance';
import { getCurrentUserApi } from '@edusense/auth-client';
import { isVSCodeEnvironment } from '../utils/vscode-env';
import { 
  ExtendedAuthContextType,
  ID_TOKEN_KEY
} from '../contexts/types';
import { AuthConfig, createDefaultConfig } from '../types/auth-config';
import { 
  createLogoutHandler,
  createFetchUserProfile,
  createFetchUserEmail,
  createStartFreshAuth
} from '../contexts/auth-utils';
import {
  createGoogleTokenExchange,
  createGoogleAuthCodeExchange, 
  createKakaoAuthCodeExchange
} from '../contexts/oauth-handlers';
import { createAuthInitializer } from '../contexts/auth-initializer';
import { resolveStorageKeys } from '../storage-keys';
import { handleRefreshTokenFlow } from '../utils/refresh';
import { resolveAccessTokenRefreshDelay } from '../utils/refresh-scheduling';

interface UseAuthProviderProps {
  config?: AuthConfig;
  ideMessagingService?: IIdeMessagingService;
  navigationService?: INavigationService;
  tokenManager?: ITokenManager;
}

type RefreshCapableTokenManager = ITokenManager & {
  setToken: (token: string | null, tokenType?: string) => void;
  setRefreshToken: (token: string | null) => void;
  getTokenType?: () => string;
  setExpiresIn?: (expiresIn: number) => void;
  addTokenChangeListener?: (listener: (token: string | null) => void) => void;
  removeTokenChangeListener?: (listener: (token: string | null) => void) => void;
  clearToken?: () => void;
};

export const useAuthProvider = ({ 
  config,
  ideMessagingService,
  navigationService,
  tokenManager: injectedTokenManager
}: UseAuthProviderProps) => {
  // 설정 병합 (앱별 설정 + 기본값)
  const finalConfig = useMemo(() => {
    const defaultConfig = createDefaultConfig(config?.appName || 'default-app');
    return { ...defaultConfig, ...config } as AuthConfig;
  }, [config]);

  const storageKeys = useMemo(() => resolveStorageKeys(finalConfig.storage), [finalConfig.storage]);
  const tokenMgr = useMemo(() => {
    const manager = injectedTokenManager || tokenManager;
    (
      manager as ITokenManager & {
        configureStorage?: (storage?: AuthConfig['storage']) => void;
      }
    ).configureStorage?.(finalConfig.storage);
    return manager;
  }, [injectedTokenManager, finalConfig.storage]);

  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    refreshToken: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  });

  // 서비스 인스턴스 설정
  const ideMessengerInstance = ideMessagingService || null;
  const navigate = navigationService?.navigate || (() => {});
  const isVSCodeEnv = isVSCodeEnvironment();
  
  // auth-client 사용 - services 대신 getCurrentUserApi 직접 사용
  const apiAxios = createAuthAxiosInstance({
    baseURL: (typeof window !== 'undefined' && (window as any).__API_BASE_URL__) ||
             import.meta.env?.VITE_API_SERVER_URL ||
             process.env.API_SERVER_URL ||
             'https://api.ugot.uk',
    enableDebug: process.env.NODE_ENV === 'development',
    authServerUrl: finalConfig.api.authServerUrl,
    oauthClientId: finalConfig.oauth2.clientId,
  });

  const apiService = {
    setToken: (_token: string, _tokenType: string = 'Bearer') => {
      // 토큰은 tokenManager + interceptor에서 자동 적용됨
    },
    getAxiosInstance: () => apiAxios,
  } as const;
  
  console.log('[AuthProvider] 🔧 초기화 정보:', {
    isVSCodeEnv,
    hasTokenManager: !!tokenMgr,
    hasApiService: !!apiService,
    hasNavigationService: !!navigationService,
    url: typeof window !== 'undefined' ? window.location.href : 'server'
  });
  
  const lastTokenRef = useRef<string | null>(null);

  // 안정적인 refs를 사용하여 무한 렌더링 방지
  const stableTokenMgr = useRef(tokenMgr);
  const stableApiService = useRef(apiService);
  const stableNavigate = useRef(navigate);
  const stableIdeMessenger = useRef(ideMessengerInstance);
  
  // refs 업데이트 (값이 실제로 변경될 때만)
  if (stableTokenMgr.current !== tokenMgr) stableTokenMgr.current = tokenMgr;
  if (stableApiService.current !== apiService) stableApiService.current = apiService;
  if (stableNavigate.current !== navigate) stableNavigate.current = navigate;
  if (stableIdeMessenger.current !== ideMessengerInstance) stableIdeMessenger.current = ideMessengerInstance;

  // 미디어 URL 정규화를 위한 기본 URL 계산
  const apiBaseFromEnv = useMemo(() => {
    const processBase = typeof process !== 'undefined' && process?.env ? process.env.API_SERVER_URL : undefined;
    const candidates = [
      (import.meta as any)?.env?.VITE_API_SERVER_URL,
      processBase,
      'https://api.ugot.uk'
    ];
    const rawBase = candidates.find((value): value is string => typeof value === 'string' && value.trim().length > 0)!.trim();
    const normalizedBase = /^https?:\/\//i.test(rawBase) ? rawBase : `https://${rawBase}`;
    return normalizedBase.replace(/\/$/, '');
  }, []);

  const ensureAbsoluteUrl = useCallback((url?: string | null): string | undefined => {
    if (!url) return undefined;
    const trimmed = url.trim();
    if (!trimmed) return undefined;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return `${apiBaseFromEnv}${path}`;
  }, [apiBaseFromEnv]);

  const normalizeUserMedia = useCallback((userData: User): User => ({
    ...userData,
    avatar: ensureAbsoluteUrl(userData.avatar),
    profileImage: ensureAbsoluteUrl(userData.profileImage)
  }), [ensureAbsoluteUrl]);

  // 유틸리티 함수 생성을 위한 공통 설정 (안정적인 참조 사용)
  const utilsConfig = useMemo(() => ({
    tokenManager: stableTokenMgr.current,
    apiService: stableApiService.current,
    isVSCodeEnv,
    ideMessengerInstance: stableIdeMessenger.current,
    navigate: stableNavigate.current,
    setAuthState,
    lastTokenRef,
    authConfig: finalConfig,
    storageKeys,
  }), [isVSCodeEnv, setAuthState, finalConfig, storageKeys]);

  // 핵심 함수들 생성
  const handleLogout = useCallback(createLogoutHandler(utilsConfig), [utilsConfig]);
  const fetchUserProfile = useCallback(createFetchUserProfile(utilsConfig), [utilsConfig]);
  const startFreshAuth = useCallback(createStartFreshAuth(utilsConfig), [utilsConfig]);
  const initializeAuth = useCallback(
    createAuthInitializer({ ...utilsConfig, fetchUserProfile }), 
    [utilsConfig, fetchUserProfile]
  );

  // OAuth2 리다이렉트 핸들러를 위한 인증 상태 설정 함수 (먼저 정의)
  const setAuthenticatedState = useCallback(async (
    user: User, 
    accessToken: string, 
    refreshToken?: string, 
    expiresIn?: number,
    tokenType: string = 'Bearer',
    idToken?: string
  ): Promise<void> => {
    console.log('[AuthProvider] 🔥 setAuthenticatedState 호출됨!', { 
      userId: user.id, 
      username: user.username,
      accessToken: accessToken.substring(0, 20) + '...',
      hasRefreshToken: !!refreshToken,
      hasIdToken: !!idToken,
      isVSCodeEnv,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(1, 4).join('\n')
    });

    // 토큰 설정
    console.log('[AuthProvider] 💾 TokenManager.setToken 호출 중...', { accessTokenLength: accessToken?.length });
    const authTokenManager = stableTokenMgr.current as RefreshCapableTokenManager;
    authTokenManager.setToken(accessToken, tokenType);

    if (typeof expiresIn === 'number' && expiresIn > 0) {
      authTokenManager.setExpiresIn?.(expiresIn);
    }
    
    console.log('[AuthProvider] 💾 ApiService.setToken 호출 중...', { tokenType });
    stableApiService.current.setToken(accessToken, tokenType);
    
    if (refreshToken) {
      console.log('[AuthProvider] 💾 TokenManager.setRefreshToken 호출 중...', { refreshTokenLength: refreshToken?.length });
      authTokenManager.setRefreshToken(refreshToken);
    }
    
    if (idToken) {
      console.log('[AuthProvider] 💾 ID 토큰 저장:', idToken.substring(0, 20) + '...');
      if (!isVSCodeEnv) {
        localStorage.setItem(storageKeys.idTokenKey || ID_TOKEN_KEY, idToken);
      }
    }

    // 인증 상태 업데이트
    console.log('[AuthProvider] 🔄 React state 업데이트 중...');
    const normalizedUser = normalizeUserMedia(user);

    setAuthState(prev => ({
      ...prev,
      isAuthenticated: true,
      user: normalizedUser,
      token: accessToken,
      refreshToken: refreshToken ?? prev.refreshToken,
      idToken
    }));

    // localStorage에 저장 (VS Code 환경이 아닌 경우에만)
    if (!isVSCodeEnv) {
      console.log('[AuthProvider] 💾 localStorage 저장 시작...', { 
        userKeyConfig: storageKeys.userKey,
        tokenKeyConfig: storageKeys.tokenKey,
        refreshTokenKeyConfig: storageKeys.refreshTokenKey
      });
      
      localStorage.setItem(storageKeys.userKey, JSON.stringify(normalizedUser));
      console.log('[AuthProvider] ✅ User 저장됨:', storageKeys.userKey);
      
      localStorage.setItem(storageKeys.tokenKey, accessToken);
      console.log('[AuthProvider] ✅ Access token 저장됨:', storageKeys.tokenKey);
      
      if (refreshToken) {
        localStorage.setItem(storageKeys.refreshTokenKey, refreshToken);
        console.log('[AuthProvider] ✅ Refresh token 저장됨:', storageKeys.refreshTokenKey);
      }
    } else {
      console.log('[AuthProvider] ⚠️ VS Code 환경이므로 localStorage 저장 건너뜀');
    }

    console.log('[AuthProvider] ✅ 인증 상태 설정 완료');
  }, [isVSCodeEnv, setAuthState, normalizeUserMedia, storageKeys]);

  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    if (isVSCodeEnv) {
      return false;
    }

    const authTokenManager = stableTokenMgr.current as RefreshCapableTokenManager;
    const refreshToken = authTokenManager.getRefreshToken();
    if (!refreshToken) {
      console.warn('[AuthProvider] Refresh token not available.');
      return false;
    }

    const result = await handleRefreshTokenFlow(
      refreshToken,
      finalConfig.api.authServerUrl,
      authTokenManager.getTokenType?.() || 'Bearer',
      finalConfig.oauth2.clientId,
    );

    if (result.success && result.accessToken) {
      const nextRefreshToken = result.refreshToken || refreshToken;
      const nextTokenType = result.tokenType || 'Bearer';

      authTokenManager.setRefreshToken(nextRefreshToken);
      if (typeof result.expiresIn === 'number' && result.expiresIn > 0) {
        authTokenManager.setExpiresIn?.(result.expiresIn);
      }
      authTokenManager.setToken(result.accessToken, nextTokenType);

      setAuthState((previous) => ({
        ...previous,
        token: result.accessToken || null,
        refreshToken: nextRefreshToken,
        isAuthenticated: Boolean(previous.user),
        error: null,
      }));
      return true;
    }

    if (result.error === 'invalid_grant') {
      authTokenManager.clearToken?.();
      setAuthState((previous) => ({
        ...previous,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: '인증 세션이 만료되었습니다.',
      }));
    }

    return false;
  }, [finalConfig.api.authServerUrl, finalConfig.oauth2.clientId, isVSCodeEnv]);

  const refreshAuth = useCallback(async (): Promise<void> => {
    await refreshAccessToken();
  }, [refreshAccessToken]);

  // OAuth 핸들러들 생성 (setAuthenticatedState 포함)
  const exchangeGoogleToken = useCallback(
    createGoogleTokenExchange({ 
      ...utilsConfig, 
      fetchUserProfile,
      setAuthenticatedState: isVSCodeEnv ? undefined : setAuthenticatedState
    }), 
    [utilsConfig, fetchUserProfile, isVSCodeEnv, setAuthenticatedState]
  );
  const exchangeGoogleAuthCode = useCallback(
    createGoogleAuthCodeExchange({ 
      ...utilsConfig, 
      fetchUserProfile,
      setAuthenticatedState: isVSCodeEnv ? undefined : setAuthenticatedState
    }), 
    [utilsConfig, fetchUserProfile, isVSCodeEnv, setAuthenticatedState]
  );
  const exchangeKakaoAuthCode = useCallback(
    createKakaoAuthCodeExchange({ 
      ...utilsConfig, 
      fetchUserProfile,
      setAuthenticatedState: isVSCodeEnv ? undefined : setAuthenticatedState
    }), 
    [utilsConfig, fetchUserProfile, isVSCodeEnv, setAuthenticatedState]
  );

  // 사용자 정보 업데이트 함수
  const updateUser = useCallback((userData: Partial<User>) => {
    setAuthState(prev => {
      if (!prev.user) {
        console.warn('[AuthProvider] 사용자가 로그인되지 않은 상태에서 업데이트 시도');
        return prev;
      }
      const updatedUser = normalizeUserMedia({ ...prev.user, ...userData });
      if (!isVSCodeEnv) {
        localStorage.setItem(storageKeys.userKey, JSON.stringify(updatedUser));
      }
      return { ...prev, user: updatedUser };
    });
  }, [isVSCodeEnv, normalizeUserMedia, storageKeys.userKey]);

  // 사용자 이메일 가져오기 함수
  const fetchUserEmail = useCallback(
    () => createFetchUserEmail(utilsConfig)(authState, updateUser),
    [utilsConfig, authState, updateUser]);

  // 초기화 effect
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Keep consumers that read the React context in sync with interceptor-driven
  // token refreshes as well as the proactive refresh path above.
  useEffect(() => {
    const authTokenManager = stableTokenMgr.current as RefreshCapableTokenManager;
    if (!authTokenManager.addTokenChangeListener || !authTokenManager.removeTokenChangeListener) {
      return;
    }

    const syncToken = (token: string | null) => {
      setAuthState((previous) => token
        ? {
            ...previous,
            token,
            refreshToken: authTokenManager.getRefreshToken() || previous.refreshToken,
          }
        : {
            ...previous,
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
          });
    };

    authTokenManager.addTokenChangeListener(syncToken);
    return () => authTokenManager.removeTokenChangeListener?.(syncToken);
  }, []);

  useEffect(() => {
    if (isVSCodeEnv || !authState.isAuthenticated) {
      return;
    }

    const authTokenManager = stableTokenMgr.current as RefreshCapableTokenManager;
    if (!authTokenManager.getRefreshToken()) {
      return;
    }

    const expiresAt = localStorage.getItem(storageKeys.tokenExpiresAtKey);
    const delay = resolveAccessTokenRefreshDelay(expiresAt);
    if (delay === null) {
      return;
    }

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const runRefresh = async () => {
      const refreshed = await refreshAccessToken();
      if (!refreshed && !cancelled && authTokenManager.getRefreshToken()) {
        retryTimer = setTimeout(runRefresh, 30_000);
      }
    };

    const refreshTimer = setTimeout(runRefresh, delay);
    return () => {
      cancelled = true;
      clearTimeout(refreshTimer);
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [
    authState.isAuthenticated,
    authState.token,
    isVSCodeEnv,
    refreshAccessToken,
    storageKeys.tokenExpiresAtKey,
  ]);

  // 컨텍스트 값을 생성하고 getter 속성 추가
  const contextValue = useMemo(() => {
    const value = {
      state: authState,
      login: async (credentials: LoginCredentials) => {
        console.warn('Credential-based login not implemented');
      },
      register: async (data: RegisterData) => {
        console.warn('Registration not implemented');
      },
      logout: handleLogout,
      refreshAuth,
      clearError: () => setAuthState(prev => ({ ...prev, error: null })),
      hasRole: (role: string) => authState.user?.role === role,
      fetchUserEmail,
      startFreshAuth: isVSCodeEnv ? undefined : startFreshAuth,
      setAuthenticatedState: isVSCodeEnv ? undefined : setAuthenticatedState,
      exchangeGoogleToken: isVSCodeEnv ? undefined : exchangeGoogleToken,
      exchangeGoogleAuthCode: isVSCodeEnv ? undefined : exchangeGoogleAuthCode,
      exchangeKakaoAuthCode: isVSCodeEnv ? undefined : exchangeKakaoAuthCode,
      // 하위 호환성을 위한 속성들
      user: authState.user,
      token: authState.token,
      isLoading: authState.isLoading,
      isInitialized: authState.isInitialized,
    };

    // isAuthenticated를 getter로 정의
    Object.defineProperty(value, 'isAuthenticated', {
      get() {
        return authState.isAuthenticated;
      },
      enumerable: true,
      configurable: true
    });

    return value as ExtendedAuthContextType;
  }, [authState, handleLogout, refreshAuth, fetchUserEmail, isVSCodeEnv, startFreshAuth, setAuthenticatedState, exchangeGoogleToken, exchangeGoogleAuthCode, exchangeKakaoAuthCode]);

  console.log('[AuthProvider] 🎯 Context Value:', {
    hasSetAuthenticatedState: !!contextValue.setAuthenticatedState,
    setAuthenticatedStateName: contextValue.setAuthenticatedState?.name,
    setAuthenticatedStateString: contextValue.setAuthenticatedState?.toString().substring(0, 100) + '...',
    isVSCodeEnv,
    authState: {
      isAuthenticated: authState.isAuthenticated,
      isLoading: authState.isLoading,
      hasUser: !!authState.user,
      hasToken: !!authState.token
    }
  });

  return contextValue;
};
